/**
 * Games API Service
 * Centralized service for all game-related API calls
 */

import { getApiUrl, getJsGamesUrl, getJsGamesV2Url } from './config';

/**
 * Normalize provider name by removing spaces
 * @param {string} provider - Provider name (e.g., 'Pragmatic Play')
 * @returns {string} Normalized provider name (e.g., 'PragmaticPlay')
 */
const normalizeProviderName = (provider) => {
  if (!provider) return provider;
  // Remove all spaces from provider name
  return provider.replace(/\s+/g, '');
};

/**
 * Fetch GIS games for a provider
 * @param {string} provider - Provider name (e.g., 'Pragmatic Play', 'Ezugi')
 * @param {number} page - Page number (default: 1)
 * @param {number} perPage - Items per page (default: 20)
 * @param {string} search - Search query (optional)
 * @param {boolean} isMobile - Whether the request is from mobile device (optional)
 * @returns {Promise<{games: Array, pagination: Object}>}
 */
export const fetchGisGames = async (provider, page = 1, perPage = 20, search = '', isMobile = false) => {
  try {
    // Normalize provider name (remove spaces) before encoding
    const normalizedProvider = normalizeProviderName(provider);
    let url = getApiUrl(`/gis/gamesgis?provider=${encodeURIComponent(normalizedProvider)}&page=${page}&limit=${perPage}&is_mobile=${isMobile}`);
    if (search && search.trim()) {
      url += `&search=${encodeURIComponent(search.trim())}`;
    }
    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    const json = await response.json();

    if (json && json.success && Array.isArray(json.data) && json.pagination) {
      return {
        games: json.data || [],
        pagination: {
          current_page: json.pagination.currentPage || page,
          total_pages: json.pagination.totalPages || 1,
          total: json.pagination.totalItems || json.data.length,
        },
      };
    } else {
      return {
        games: json.data || [],
        pagination: { current_page: page, total_pages: 1, total: json.data?.length || 0 },
      };
    }
  } catch (error) {
    console.error(`fetchGisGames error for ${provider}:`, error);
    return { games: [], pagination: { current_page: page, total_pages: 1 } };
  }
};

/**
 * Fetch GIS games by game type (category)
 * @param {string} type - Game type (e.g., 'crash', 'baccarat', 'roulette')
 * @param {boolean} isMobile - Whether the request is from mobile device (optional)
 * @returns {Promise<{games: Array}>}
 */
export const fetchGisGamesByType = async (type, isMobile = false, page = 1, perPage = 24) => {
  try {
    const url = getApiUrl(`/gis/gamesgis?type=${encodeURIComponent(type)}&page=${page}&limit=${perPage}&is_mobile=${isMobile}`);
    const response = await fetch(url);
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const json = await response.json();
    const pagination = json.pagination || {};
    return {
      games: json.data || [],
      hasMore: pagination.currentPage < pagination.totalPages,
      pagination,
    };
  } catch (error) {
    console.error(`fetchGisGamesByType error for type "${type}":`, error);
    return { games: [], hasMore: false, pagination: {} };
  }
};

/**
 * Search games across all providers
 * @param {string} query - Search query
 * @param {number} page - Page number (default: 1)
 * @param {number} perPage - Items per page (default: 24)
 * @param {boolean} isMobile - Whether the request is from mobile device
 * @returns {Promise<{games: Array, hasMore: boolean, pagination: Object}>}
 */
export const searchGisGames = async (query, page = 1, perPage = 24, isMobile = false) => {
  try {
    const url = getApiUrl(`/gis/gamesgis?page=${page}&limit=${perPage}&is_mobile=${isMobile}&search=${encodeURIComponent(query)}`);
    const response = await fetch(url);
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const json = await response.json();
    const pagination = json.pagination || {};
    return {
      games: json.data || [],
      hasMore: pagination.currentPage < pagination.totalPages,
      pagination,
    };
  } catch (error) {
    console.error(`searchGisGames error for query "${query}":`, error);
    return { games: [], hasMore: false, pagination: {} };
  }
};

/**
 * Fetch hot games
 * @param {number} page - Page number (default: 1)
 * @param {number} perPage - Items per page (default: 20)
 * @param {boolean} isMobile - Whether the request is from mobile device (optional)
 * @returns {Promise<Array>}
 */
export const fetchHotGames = async (page = 1, perPage = 20, isMobile = false) => {
  try {
    const url = getApiUrl(`/gis/hotgames?page=${page}&limit=${perPage}&is_mobile=${isMobile}`);

    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    const result = await response.json();
    return result.data || result || [];
  } catch (error) {
    console.error('Error fetching hot games:', error);
    return [];
  }
};

/**
 * Fetch Pragmatic Play slots games from JS Games API (like old code)
 * @param {number} page - Page number (default: 1)
 * @param {number} perPage - Items per page (default: 20)
 * @param {boolean} isMobile - Whether the request is from mobile device (optional)
 * @returns {Promise<Array>}
 */
export const fetchPragmaticSlots = async (page = 1, perPage = 20, isMobile = false) => {
  try {
    // Use JS Games API endpoint (like old code: https://api.cryptmet.com/jsGames/games?vendor=pragmatic&page=1&per_page=20)
    const url = getApiUrl(`/gis/gamesgis?provider=PragmaticPlay&page=${page}&limit=${perPage}&is_mobile=${isMobile}`);
    const response = await fetch(url);

    // console.log("Pragmatic Slots Response:", response);

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    const result = await response.json();

    // New API structure: result.success && result.data (array of games)
    if (result.success && result.data && Array.isArray(result.data)) {
      // Transform games to match expected structure
      return result.data.map(game => ({
        id: game.uuid,
        uuid: game.uuid,
        game_uid: game.uuid,
        name: game.name,
        game_name: game.name,
        game_icon: game.image,
        image: game.image,
        game_type: game.type,
        provider: game.provider || 'PragmaticPlay',
        source: 'jsgames',
      }));
    }

    // Fallback to old structure for backward compatibility
    if (result.success && result.payload && result.payload.games) {
      return result.payload.games.map(game => ({
        id: game.id || game.game_uid,
        uuid: game.game_uid || game.id,
        game_uid: game.game_uid,
        name: game.game_name || game.name,
        game_name: game.game_name || game.name,
        game_icon: game.game_icon || game.image,
        image: game.game_icon || game.image,
        game_type: game.game_type || game.type,
        provider: game.vendor || 'pragmatic',
        source: 'jsgames',
      }));
    }

    return [];
  } catch (error) {
    console.error('Error fetching pragmatic slots:', error);
    return [];
  }
};

/**
 * Launch a GIS game
 * @param {Object} gameData - Game data with uuid, player info, etc.
 * @returns {Promise<{url: string}|{message: string}>}
 */
export const launchGisGame = async (gameData) => {
  try {
    const response = await fetch(getApiUrl('/gis/games/init'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(gameData),
    });

    const result = await response.json();

    if (result.url) {
      return { url: result.url };
    } else {
      return { message: result.message || 'Failed to launch game' };
    }
  } catch (error) {
    console.error('Error launching GIS game:', error);
    return { message: error.message || 'Unexpected error' };
  }
};

/**
 * Launch a JS game
 * @param {Object} gameData - Game data with game_uid, user_id, etc.
 * @returns {Promise<{game_launch_url: string}|{message: string}>}
 */
export const launchJsGame = async (gameData) => {
  try {
    // Use JS Games V2 API for launching (like old code)
    const response = await fetch(getJsGamesV2Url('/launch'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(gameData),
    });

    const result = await response.json();

    if (result.success && result.data?.game_launch_url) {
      return { game_launch_url: result.data.game_launch_url };
    } else {
      return { message: result.message || 'Failed to launch game' };
    }
  } catch (error) {
    console.error('Error launching JS game:', error);
    return { message: error.message || 'Unexpected error' };
  }
};
